import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useTransition,
  type FormEvent,
  type ReactNode,
} from "react"
import { useLocation, useNavigate } from "react-router-dom"

import { callWeeklyPredictionAPI, type IntervalData } from "./api"

import { WeeklyCalculatorContext } from "./useWeeklyCalculator"
import type { WeeklyCalculatorFormErrors, WeeklyCalculatorFormValues } from "./types/form"
import { getDefaultWeeklyFormValues } from "./utils/form"
import { weeklyFormSchema } from "./validation"
import { useDebounce } from "@/shared/hooks/useDebounce"
import { loadFromStorage, saveToStorage } from "@/shared/lib/calculator-storage"
import {
  WEEKLY_STORAGE_KEY,
  type WeeklySavedCalculation,
  weeklySavedCalculationSchema,
} from "./types/storage"

export type WeeklyCalculatorStatus = "idle" | "calculating" | "complete" | "error"

export type WeeklyResult = {
  agentsFte: number
  agentsPer30MinsMax: number
  callsPer30MinsMax: number
  callsPerWeek: number
  intervals: IntervalData[]
}

export type WeeklyCalculatorContextValue = {
  advancedOpen: boolean
  formErrors: WeeklyCalculatorFormErrors
  formValues: WeeklyCalculatorFormValues
  handleFieldChange: (field: keyof WeeklyCalculatorFormValues, value: string) => void
  handleSubmit: (event: FormEvent<HTMLFormElement>) => void
  hideResults: (options?: { replace?: boolean }) => void
  isPending: boolean
  result: WeeklyResult | null
  resultsVisible: boolean
  showResults: (options?: { replace?: boolean }) => void
  status: WeeklyCalculatorStatus
  toggleAdvanced: () => void
  validateForm: () => { isValid: boolean; firstErrorField?: string }
  error: string | null
}

interface WeeklyCalculatorProviderProps {
  children: ReactNode
}

// URL parameter keys (abbreviated to keep URLs short)
// Note: period field removed - calculator type determined by route
const FORM_QUERY_KEYS: ReadonlyArray<keyof WeeklyCalculatorFormValues> = [
  "incomingContacts",
  "averageHandleTimeSeconds",
  "requiredServiceLevel",
  "targetAnswerTimeSeconds",
  "shrinkage",
  "maxOccupancy",
  "averagePatienceSeconds",
  "workingHoursPerWeek",
  "reportingInterval",
  "hoursOpenPerDay",
  "weekStart",
  "distributionMonday",
  "distributionTuesday",
  "distributionWednesday",
  "distributionThursday",
  "distributionFriday",
  "distributionSaturday",
  "distributionSunday",
]

// Map form field names to URL parameter names
const FIELD_TO_PARAM_MAP: Record<keyof WeeklyCalculatorFormValues, string> = {
  incomingContacts: "ic",
  averageHandleTimeSeconds: "aht",
  requiredServiceLevel: "sl",
  targetAnswerTimeSeconds: "tat",
  shrinkage: "sh",
  maxOccupancy: "occ",
  averagePatienceSeconds: "ap",
  workingHoursPerWeek: "hw",
  reportingInterval: "ri",
  hoursOpenPerDay: "hpd",
  weekStart: "ws",
  distributionMonday: "dm",
  distributionTuesday: "dt",
  distributionWednesday: "dw",
  distributionThursday: "dth",
  distributionFriday: "df",
  distributionSaturday: "dsa",
  distributionSunday: "dsu",
}

const RESULTS_QUERY_PARAM = "results"
const RESULTS_HASH = "#results"

/**
 * Read form values from URL parameters
 */
const readFormValuesFromParams = (params: URLSearchParams): WeeklyCalculatorFormValues | null => {
  const values: Partial<WeeklyCalculatorFormValues> = {}

  for (const key of FORM_QUERY_KEYS) {
    const paramName = FIELD_TO_PARAM_MAP[key]
    const value = params.get(paramName)

    if (value === null) {
      return null
    }

    values[key] = value
  }

  return values as WeeklyCalculatorFormValues
}

/**
 * Set form values as URL parameters
 */
const setFormParams = (params: URLSearchParams, formValues: WeeklyCalculatorFormValues) => {
  for (const key of FORM_QUERY_KEYS) {
    const paramName = FIELD_TO_PARAM_MAP[key]
    params.set(paramName, formValues[key])
  }
}

/**
 * Delete all form parameters from URL
 */
const deleteFormParams = (params: URLSearchParams) => {
  for (const paramName of Object.values(FIELD_TO_PARAM_MAP)) {
    params.delete(paramName)
  }
}

/**
 * Check if results should be visible based on URL
 */
const getResultsVisibilityFromLocation = (search: string, hash: string): boolean => {
  const params = new URLSearchParams(search)

  if (params.get(RESULTS_QUERY_PARAM) === "true") {
    return true
  }

  const normalizedHash = hash.startsWith("#") ? hash : `#${hash}`
  return normalizedHash === RESULTS_HASH
}

/**
 * Normalize hash (add # prefix if missing)
 */
const normalizeHash = (hash: string): string => {
  if (!hash) {
    return ""
  }

  return hash.startsWith("#") ? hash : `#${hash}`
}

/**
 * Load saved calculation from localStorage
 */
const readSavedCalculation = (): WeeklySavedCalculation | null => {
  return loadFromStorage(WEEKLY_STORAGE_KEY, weeklySavedCalculationSchema)
}

/**
 * Persist calculation to localStorage
 */
const persistCalculation = (payload: WeeklySavedCalculation | null) => {
  if (!payload) {
    return
  }

  saveToStorage(WEEKLY_STORAGE_KEY, payload)
}

export function WeeklyCalculatorProvider({ children }: WeeklyCalculatorProviderProps) {
  const location = useLocation()
  const navigate = useNavigate()
  const [isPending, startTransition] = useTransition()

  const pendingLocationRef = useRef<{ search: string; hash: string } | null>(null)
  const formValuesRef = useRef<WeeklyCalculatorFormValues>(getDefaultWeeklyFormValues())
  const calculationInProgressRef = useRef<boolean>(false)

  // Initialize state from saved calculation using lazy initializers
  const [formValues, setFormValues] = useState<WeeklyCalculatorFormValues>(() => {
    const saved = readSavedCalculation()
    if (saved?.formValues) {
      return saved.formValues
    }
    return getDefaultWeeklyFormValues()
  })

  const [formErrors, setFormErrors] = useState<WeeklyCalculatorFormErrors>({})

  const [advancedOpen, setAdvancedOpen] = useState(() => {
    return readSavedCalculation()?.advancedOpen ?? false
  })

  const [status, setStatus] = useState<WeeklyCalculatorStatus>(() => {
    const saved = readSavedCalculation()
    return saved?.result ? "complete" : "idle"
  })

  const [result, setResult] = useState<WeeklyResult | null>(() => {
    const saved = readSavedCalculation()
    if (saved?.result) {
      // Convert summary back to full result (without intervals)
      return {
        ...saved.result,
        intervals: [],
      }
    }
    return null
  })

  const [error, setError] = useState<string | null>(null)

  // Derive results visibility from URL
  const resultsVisible = useMemo(
    () => getResultsVisibilityFromLocation(location.search, location.hash),
    [location.hash, location.search],
  )

  // Keep ref in sync with formValues for URL updates
  useEffect(() => {
    formValuesRef.current = formValues
  }, [formValues])

  // Debounce form values (1 second delay for auto-save)
  const debouncedFormValues = useDebounce(formValues, 1000)

  const handleFieldChange = useCallback(
    (field: keyof WeeklyCalculatorFormValues, value: string) => {
      setFormValues((prev) => ({
        ...prev,
        [field]: value,
      }))

      // Clear error for this field when user starts editing
      if (formErrors[field]) {
        setFormErrors((prev) => {
          const next = { ...prev }
          delete next[field]
          return next
        })
      }

      // Clear API error when user makes changes
      if (error) {
        setError(null)
      }
    },
    [formErrors, error],
  )

  const toggleAdvanced = useCallback(() => {
    setAdvancedOpen((prev) => !prev)
  }, [])

  // Auto-save to localStorage (debounced)
  useEffect(() => {
    persistCalculation({
      version: 1,
      formValues: debouncedFormValues as any,
      result: result
        ? {
            agentsFte: result.agentsFte,
            agentsPer30MinsMax: result.agentsPer30MinsMax,
            callsPer30MinsMax: result.callsPer30MinsMax,
            callsPerWeek: result.callsPerWeek,
          }
        : null,
      advancedOpen,
      status: status === "complete" ? "complete" : "idle",
    })
  }, [debouncedFormValues, result, advancedOpen, status])

  // Set results visibility in URL
  const setResultsVisibility = useCallback(
    (visible: boolean, options?: { replace?: boolean; formValues?: WeeklyCalculatorFormValues }) => {
      const params = new URLSearchParams(location.search)
      const normalizedHash = normalizeHash(location.hash)
      const currentlyVisible =
        params.get(RESULTS_QUERY_PARAM) === "true" || normalizedHash === RESULTS_HASH

      if (visible) {
        params.set(RESULTS_QUERY_PARAM, "true")
        const valuesToPersist = options?.formValues ?? formValuesRef.current

        if (valuesToPersist) {
          setFormParams(params, valuesToPersist)
        }
      } else {
        params.delete(RESULTS_QUERY_PARAM)
        deleteFormParams(params)
      }

      const nextSearch = params.toString()
      const nextSearchString = nextSearch ? `?${nextSearch}` : ""
      const nextHash = visible ? RESULTS_HASH : ""
      const normalizedNextHash = nextHash ? normalizeHash(nextHash) : ""

      if (
        currentlyVisible === visible &&
        location.search === nextSearchString &&
        normalizedHash === normalizedNextHash
      ) {
        return
      }

      pendingLocationRef.current = { search: nextSearchString, hash: normalizedNextHash }

      navigate(
        {
          pathname: location.pathname,
          search: nextSearchString,
          hash: normalizedNextHash,
        },
        {
          replace: options?.replace ?? false,
        },
      )
    },
    [location.hash, location.pathname, location.search, navigate],
  )

  const showResults = useCallback(
    (options?: { replace?: boolean }) => {
      setResultsVisibility(true, {
        replace: options?.replace ?? resultsVisible,
        formValues: formValues,
      })
    },
    [formValues, resultsVisible, setResultsVisibility],
  )

  const hideResults = useCallback(
    (options?: { replace?: boolean }) => {
      if (!resultsVisible && !options?.replace) {
        return
      }

      setResultsVisibility(false, {
        replace: options?.replace ?? false,
      })
    },
    [resultsVisible, setResultsVisibility],
  )

  const validateForm = useCallback((): { isValid: boolean; firstErrorField?: string } => {
    const validationResult = weeklyFormSchema.safeParse(formValues)

    if (!validationResult.success) {
      const errors: WeeklyCalculatorFormErrors = {}
      let firstErrorField: string | undefined

      validationResult.error.issues.forEach((issue) => {
        if (issue.path.length > 0) {
          const fieldName = issue.path[0] as keyof WeeklyCalculatorFormValues
          errors[fieldName] = issue.message
          if (!firstErrorField) {
            firstErrorField = fieldName
          }
        }
      })

      setFormErrors(errors)
      return { isValid: false, firstErrorField }
    }

    setFormErrors({})
    return { isValid: true }
  }, [formValues])

  // URL synchronization effect - handles loading from URL params
  useEffect(() => {
    const normalizedHash = normalizeHash(location.hash)
    const expected = pendingLocationRef.current

    if (expected && expected.search === location.search && expected.hash === normalizedHash) {
      pendingLocationRef.current = null
      return
    }

    pendingLocationRef.current = null

    const params = new URLSearchParams(location.search)
    const formValuesFromParams = readFormValuesFromParams(params)

    if (formValuesFromParams) {
      // Use queueMicrotask to avoid synchronous setState in effect
      queueMicrotask(() => {
        setFormValues((previous) => {
          if (FORM_QUERY_KEYS.every((key) => previous[key] === formValuesFromParams[key])) {
            return previous
          }

          return { ...formValuesFromParams }
        })
        setFormErrors({})
      })
    }

    const shouldShowResults =
      params.get(RESULTS_QUERY_PARAM) === "true" || normalizedHash === RESULTS_HASH

    if (!shouldShowResults) {
      return
    }

    if (!formValuesFromParams) {
      // Use queueMicrotask to avoid synchronous setState in effect
      queueMicrotask(() => {
        setResult(null)
        setStatus("idle")
        setResultsVisibility(false, { replace: true })
      })
      return
    }

    const validationResult = weeklyFormSchema.safeParse(formValuesFromParams)

    if (!validationResult.success) {
      // Use queueMicrotask to avoid synchronous setState in effect
      queueMicrotask(() => {
        const errors: WeeklyCalculatorFormErrors = {}

        validationResult.error.issues.forEach((issue) => {
          if (issue.path.length > 0) {
            const fieldName = issue.path[0] as keyof WeeklyCalculatorFormValues
            errors[fieldName] = issue.message
          }
        })

        setFormErrors(errors)
        setStatus("idle")
        setResultsVisibility(false, { replace: true })
      })
      return
    }

    // Check if calculation already in progress
    if (calculationInProgressRef.current) {
      return
    }

    // Check if we already have results for these inputs (with intervals data)
    if (result && status === "complete" && result.intervals.length > 0) {
      // Results already present with full data, don't recalculate
      return
    }

    // Set guard to prevent duplicate calculations
    calculationInProgressRef.current = true

    // Use queueMicrotask to avoid synchronous setState in effect
    queueMicrotask(() => {
      setStatus("calculating")
    })

    startTransition(async () => {
      try {
        const apiResult = await callWeeklyPredictionAPI(formValuesFromParams)

        if (apiResult) {
          const newResult: WeeklyResult = {
            agentsFte: apiResult.result.agentsFte,
            agentsPer30MinsMax: apiResult.result.agentsPer30MinsMax,
            callsPer30MinsMax: apiResult.result.callsPer30MinsMax,
            callsPerWeek: apiResult.result.callsPerWeek,
            intervals: apiResult.intervals,
          }

          calculationInProgressRef.current = false
          setResult(newResult)
          setStatus("complete")

          // Immediate save after calculation
          persistCalculation({
            version: 1,
            formValues: formValuesFromParams as any,
            result: {
              agentsFte: newResult.agentsFte,
              agentsPer30MinsMax: newResult.agentsPer30MinsMax,
              callsPer30MinsMax: newResult.callsPer30MinsMax,
              callsPerWeek: newResult.callsPerWeek,
            },
            advancedOpen,
            status: "complete",
          })
        } else {
          calculationInProgressRef.current = false
          setError("Failed to calculate staffing. Please try again.")
          setStatus("error")
        }
      } catch (err) {
        calculationInProgressRef.current = false
        console.error("Weekly calculation error:", err)
        setError("An unexpected error occurred. Please try again.")
        setStatus("error")
      }
    })
  }, [location.hash, location.search, setResultsVisibility, advancedOpen, startTransition])

  // Safety guard - prevent hiding results when they should be visible
  useEffect(() => {
    if (!resultsVisible) {
      return
    }

    // Don't hide during calculation
    if (calculationInProgressRef.current || status === "calculating") {
      return
    }

    if (result) {
      return
    }

    // Only hide if in stable state with no results
    hideResults({ replace: true })
  }, [hideResults, result, resultsVisible, status])

  const handleSubmit = useCallback(
    (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault()
      const { isValid, firstErrorField } = validateForm()

      if (!isValid) {
        if (firstErrorField) {
          // Scroll to the first error field
          const element = document.getElementById(firstErrorField)
          if (element) {
            element.scrollIntoView({ behavior: "smooth", block: "center" })
            element.focus()
          }
        }
        return
      }

      calculationInProgressRef.current = true
      setStatus("calculating")
      setError(null)
      const snapshotFormValues: WeeklyCalculatorFormValues = { ...formValues }

      // Update URL with form params and show results
      setResultsVisibility(true, {
        replace: false,
        formValues: snapshotFormValues,
      })

      startTransition(async () => {
        try {
          const apiResult = await callWeeklyPredictionAPI(snapshotFormValues)

          if (apiResult) {
            const newResult: WeeklyResult = {
              agentsFte: apiResult.result.agentsFte,
              agentsPer30MinsMax: apiResult.result.agentsPer30MinsMax,
              callsPer30MinsMax: apiResult.result.callsPer30MinsMax,
              callsPerWeek: apiResult.result.callsPerWeek,
              intervals: apiResult.intervals,
            }

            calculationInProgressRef.current = false
            setResult(newResult)
            setStatus("complete")

            // Immediate save after calculation
            persistCalculation({
              version: 1,
              formValues: snapshotFormValues as any,
              result: {
                agentsFte: newResult.agentsFte,
                agentsPer30MinsMax: newResult.agentsPer30MinsMax,
                callsPer30MinsMax: newResult.callsPer30MinsMax,
                callsPerWeek: newResult.callsPerWeek,
              },
              advancedOpen,
              status: "complete",
            })
          } else {
            calculationInProgressRef.current = false
            setError("Failed to calculate staffing. Please try again.")
            setStatus("error")
          }
        } catch (err) {
          calculationInProgressRef.current = false
          console.error("Weekly calculation error:", err)
          setError("An unexpected error occurred. Please try again.")
          setStatus("error")
        }
      })
    },
    [validateForm, formValues, setResultsVisibility, advancedOpen, startTransition],
  )

  const value = useMemo<WeeklyCalculatorContextValue>(
    () => ({
      advancedOpen,
      formErrors,
      formValues,
      handleFieldChange,
      handleSubmit,
      hideResults,
      isPending,
      result,
      resultsVisible,
      showResults,
      status,
      toggleAdvanced,
      validateForm,
      error,
    }),
    [
      advancedOpen,
      formErrors,
      formValues,
      handleFieldChange,
      handleSubmit,
      hideResults,
      isPending,
      result,
      resultsVisible,
      showResults,
      status,
      toggleAdvanced,
      validateForm,
      error,
    ],
  )

  return (
    <WeeklyCalculatorContext.Provider value={value}>{children}</WeeklyCalculatorContext.Provider>
  )
}
